import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ResultCard from "./components/ResultCard";
import SearchForm from "./components/SearchForm";
import { dataAsOf, buildDrinkCosts, indexStores, loadDataset } from "./data";
import { calculate, cheapestByStore } from "./engine";
import { durationLabel, formatDate } from "./format";
import type { CalcParams, Dataset } from "./types";

const DEFAULT_PARAMS: CalcParams = {
  area: "全エリア",
  dayType: "月～木",
  startTime: "14:00",
  durationMinutes: 120,
  numPeople: 2,
  audience: "一般",
  isMember: true,
  drinkPref: "こだわらない(最安)",
  oneOrderDrinkCost: 450,
  includeOptions: true,
};

const AREAS = ["全エリア", "手稲", "麻生", "石狩"];

function readParamsFromUrl(): CalcParams {
  const query = new URLSearchParams(window.location.search);
  const people = Number(query.get("people"));
  const duration = Number(query.get("duration"));
  return {
    area: AREAS.includes(query.get("area") ?? "") ? (query.get("area") as string) : DEFAULT_PARAMS.area,
    dayType: (query.get("day") as CalcParams["dayType"]) ?? DEFAULT_PARAMS.dayType,
    startTime: query.get("start") ?? DEFAULT_PARAMS.startTime,
    durationMinutes: Number.isFinite(duration) && duration > 0 ? duration : DEFAULT_PARAMS.durationMinutes,
    numPeople: Number.isFinite(people) && people > 0 ? Math.min(12, people) : DEFAULT_PARAMS.numPeople,
    audience: (query.get("audience") as CalcParams["audience"]) ?? DEFAULT_PARAMS.audience,
    isMember: query.get("member") !== "0",
    drinkPref: (query.get("drink") as CalcParams["drinkPref"]) ?? DEFAULT_PARAMS.drinkPref,
    oneOrderDrinkCost: DEFAULT_PARAMS.oneOrderDrinkCost,
    includeOptions: query.get("options") !== "0",
  };
}

function writeParamsToUrl(params: CalcParams): void {
  const query = new URLSearchParams({
    area: params.area,
    day: params.dayType,
    start: params.startTime,
    duration: String(params.durationMinutes),
    people: String(params.numPeople),
    audience: params.audience,
    member: params.isMember ? "1" : "0",
    drink: params.drinkPref,
    options: params.includeOptions ? "1" : "0",
  });
  window.history.replaceState(null, "", `${window.location.pathname}?${query.toString()}`);
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; dataset: Dataset };

export default function App() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [params, setParams] = useState<CalcParams>(() => readParamsFromUrl());
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    loadDataset()
      .then((dataset) => {
        if (active) setState({ status: "ready", dataset });
      })
      .catch((error: unknown) => {
        if (active) setState({ status: "error", message: (error as Error).message });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    writeParamsToUrl(params);
  }, [params]);

  const update = useCallback((patch: Partial<CalcParams>) => {
    setParams((current) => ({ ...current, ...patch }));
  }, []);

  const results = useMemo(() => {
    if (state.status !== "ready") return [];
    const drinkCosts = buildDrinkCosts(state.dataset);
    return cheapestByStore(calculate(state.dataset.plans, { ...params, drinkCosts }));
  }, [state, params]);

  const stores = useMemo(
    () => (state.status === "ready" ? indexStores(state.dataset) : new Map()),
    [state],
  );
  const asOf = state.status === "ready" ? formatDate(dataAsOf(state.dataset)) : null;

  return (
    <div className="min-h-screen">
      <header className="bg-gradient-to-br from-brand-500 to-brand-700 text-white">
        <div className="mx-auto max-w-3xl px-4 pb-5 pt-6">
          <h1 className="text-xl font-bold tracking-wide">🎤 カラオケ料金比較</h1>
          <p className="mt-1 text-[0.8rem] text-white/90">
            手稲・麻生・石狩の店舗から、いまの条件で一番安いプランを探します。
          </p>
          {asOf && (
            <p className="mt-2 inline-block rounded-full bg-white/15 px-2.5 py-0.5 text-[0.7rem]">
              データ基準日 {asOf}
            </p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        <SearchForm
          params={params}
          areas={AREAS}
          onChange={update}
          onSubmit={() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />

        <div ref={resultsRef} className="scroll-mt-4">
          {state.status === "loading" && (
            <div className="mt-6 space-y-3" aria-busy="true" aria-live="polite">
              <p className="text-sm text-slate-500">料金データを読み込んでいます…</p>
              {[0, 1, 2].map((key) => (
                <div key={key} className="h-28 animate-pulse rounded-2xl bg-slate-200/70" />
              ))}
            </div>
          )}

          {state.status === "error" && (
            <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              <p className="font-semibold">データを読み込めませんでした</p>
              <p className="mt-1">{state.message}</p>
              <button
                type="button"
                className="mt-3 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
                onClick={() => window.location.reload()}
              >
                再読み込み
              </button>
            </div>
          )}

          {state.status === "ready" && (
            <>
              <p className="mt-5 text-xs text-slate-500" aria-live="polite">
                {params.area} / {params.dayType} / {params.startTime}〜 /{" "}
                {durationLabel(params.durationMinutes)} / {params.numPeople}名 / {params.audience} /{" "}
                {params.drinkPref} — {results.length}店舗
              </p>

              {results.length === 0 ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
                  条件に合うプランが見つかりませんでした。時間や人数を変えてお試しください。
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  {results.map((result, index) => (
                    <ResultCard
                      key={`${result.storeName}-${result.planName}`}
                      result={result}
                      rank={index + 1}
                      store={stores.get(result.storeName)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-5 text-[0.72rem] leading-relaxed text-slate-500">
          <p>料金は変更されることがあります。利用前に店舗の公式情報を確認してください。</p>
          <p className="mt-1">データ: 店舗公式の料金表（確認日は各カードに表示）</p>
        </div>
      </footer>
    </div>
  );
}
